//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState } from 'react';
import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { FormControl, FormControlLabel, makeStyles, Radio, RadioGroup } from '@material-ui/core';
import { Grid } from '@material-ui/core';
// import { useLocation } from 'react-router-dom';
import SimpleBar from 'simplebar-react';
import { useFormik } from 'formik';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { AppContext, LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import useSimpleBarRef from '../common/hooks/useSimpleBarRef';
import { BASE_URL, DASHBOARD_ID_MY_SETTINGS, REQUIRED, USER_TYPE_EMP, USER_TYPE_JS } from '../common/constants';
import { PATH_DASHBOARD, PATH_HOME, QUERY_PARAM_NAME_HIDE_USER_TYPE, QUERY_PARAM_NAME_USER_TYPE } from '../system/constants';
import { PLACEHOLDER_FIELD_EMAIL, PLACEHOLDER_FIELD_FIRST_NAME, PLACEHOLDER_FIELD_LAST_NAME } from '../common/placeholderHelper';
import { createUserWithSocialMediaSignIn } from '../common/services/authServices';
import { useQuery } from '../common/hooks/useQuery';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import TopNav from '../topNav/TopNav';
import AfitdButton from '../common/elemental/AfitdButton';
import AfitdTextField from '../common/elemental/AfitdTextField';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _size from 'lodash/size';
import _split from 'lodash/split';
import _forEach from 'lodash/forEach';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const textFieldStyles = { marginTop: '10px', marginBottom: '30px' };
    const submitButtonContainerStyles = { marginTop: 38 };
    const submitButtonStyles = { textTransform: 'capitalize', width: '100%', padding: 17, fontSize: 18, fontWeight: 500 };
    const logoutButtonContainerStyles = { marginTop: 38 };
    const logoutButtonStyles = { textTransform: 'capitalize', width: '100%', padding: 17, fontSize: 18, fontWeight: 500 };

    return makeStyles(theme => ({
        root: {},
        pageContainer: { backgroundColor: '#FAFAFC', overflow: 'hidden', height: '100%', minHeight: '100%', flexWrap: 'unset' },
        pageHeader: {
            maxHeight: '81px',
            // [theme.breakpoints.down('md')]: { maxHeight: '134px' }, // commented out because no more second row menu -el 20221022
            // [theme.breakpoints.down('sm')]: { maxHeight: '81px' },
        },
        pageContent: {
            flex: 1,
            overflowY: 'hidden',
        },
        tempContainer: { alignItems: 'center', marginTop: 50, paddingLeft: 16, paddingRight: 16 },

        fieldLabel: { fontWeight: 'bold' },
        firstNameContainer: { paddingRight: '10px', [theme.breakpoints.down('sm')]: { paddingRight: 0 } },
        firstName: { ...textFieldStyles },
        lastNameContainer: { paddingLeft: '10px', [theme.breakpoints.down('sm')]: { paddingLeft: 0 } },
        lastName: { ...textFieldStyles },
        email: { ...textFieldStyles },
        checkBoxContainer: { marginLeft: -23 },
        radioContainer: { marginLeft: 25 },
        createAccountButtonContainer: { ...submitButtonContainerStyles },
        createAccountButton: { ...submitButtonStyles },
        logoutButtonContainer: { ...logoutButtonContainerStyles },
        logoutButton: { ...logoutButtonStyles },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        formik,
        isUserTypeJsRadioSelected,
        setIsUserTypeJsRadioSelected,
        handleClickLogoutButton,
    } = params;

    const defns = {
        pageContainer: { container: true, direction: 'column' },
        pageHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        pageContent: { item: true, container: true },
        tempContainer: { item: true, container: true, direction: 'column' },

        createAccountForm: {
            onSubmit: formik.handleSubmit,
        },
        formInputContainer: { container: true },
        formInputItem: { item: true, lg: 6, md: 6, sm: 12, xs: 12 },
        firstNameContainer: { item: true, lg: 6, md: 6, sm: 12, xs: 12 },
        lastNameContainer: { item: true, lg: 6, md: 6, sm: 12, xs: 12 },
        firstName: {
            placeholder: PLACEHOLDER_FIELD_FIRST_NAME,
            id: 'firstName',
            ...formik.getFieldProps('firstName'),
            error: formik.touched.firstName && !!formik.errors?.firstName,
            helperText: formik.touched.firstName && formik.errors?.firstName,
        },
        lastName: {
            placeholder: PLACEHOLDER_FIELD_LAST_NAME,
            id: 'lastName',
            ...formik.getFieldProps('lastName'),
            error: formik.touched.lastName && !!formik.errors?.lastName,
            helperText: formik.touched.lastName && formik.errors?.lastName,
        },
        email: {
            placeholder: PLACEHOLDER_FIELD_EMAIL,
            id: 'email',
            ...formik.getFieldProps('email'),
            // error: formik.touched.email && !!formik.errors?.email,
            helperText: formik.touched.email && formik.errors?.email,
            disabled: true,
        },
        userTypeJsRadioButton: {
            value: USER_TYPE_JS,
            label: 'I am a Job Seeker',
            style: { color: isUserTypeJsRadioSelected ? 'black' : 'rgba(17, 30, 20, 0.4)' },
            onClick: () => setIsUserTypeJsRadioSelected(true),
        },
        userTypeEmpRadioButton: {
            value: USER_TYPE_EMP,
            label: 'I am an Employer/ Recruiter',
            style: { color: isUserTypeJsRadioSelected ? 'rgba(17, 30, 20, 0.4)' : ' black' },
            onClick: () => setIsUserTypeJsRadioSelected(false),
        },
        createAccountButton: {
            variant: 'contained',
            color: 'secondary',
            type: 'submit',
            // onClick: handleClickCreateAccountButton,
        },
        logoutButton: {
            variant: 'contained',
            color: 'default',
            onClick: handleClickLogoutButton,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const signUpWithSocialMedia = async ({ userId, providerId, data, isUserTypeJsRadioSelected, history, redirectPathname }) => {
    const firstName = _get(data, 'firstName');
    const lastName = _get(data, 'lastName');
    const email = _get(data, 'email');

    console.log('signUpWithSocialMedia', { email, firstName, lastName, isUserTypeJsRadioSelected });

    try {
        await createUserWithSocialMediaSignIn({ userId, email, firstName, lastName, isJs: isUserTypeJsRadioSelected, providerId });

        // after creating the account, redirect to where the user was originally navigating to;
        //   but if user was going to the Home page, redirect to My Settings instead.
        if (redirectPathname && redirectPathname !== `${BASE_URL}${PATH_HOME}` && redirectPathname !== '/') history.push(redirectPathname);
        else history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_SETTINGS}`);
    } catch (error) {
        throw error;
    }
};

const validate = values => {
    const errors = {};

    const fieldNames = ['email', 'firstName', 'lastName'];

    // required fields
    _forEach(fieldNames, fieldName => {
        if (!_get(values, fieldName)) {
            _set(errors, fieldName, REQUIRED);
        }
    });

    return errors;
};

const getName = displayName => {
    const nameParts = _split(displayName, ' ');
    const len = _size(nameParts);
    const lastItemIndex = len > 1 ? len - 1 : -1;
    return { firstName: _get(nameParts, '[0]'), lastName: _get(nameParts, `[${lastItemIndex}]`) };
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const CreateAccountLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles()();
    const appContext = useContext(AppContext);
    const { authRedirectResult, authUser } = appContext;
    const context = useContext(LayoutContext);
    const { simpleBarRef: pageContentSimpleBarEl, scrollToTop: scrollToPageTop } = useSimpleBarRef();

    const history = useHistory();
    // const location = useLocation();
    // const subPageId = _get(location, 'state.subPageId');
    const query = useQuery();
    const userTypeQueryParam = query.get(QUERY_PARAM_NAME_USER_TYPE);
    const hideUserTypeQueryParam = query.get(QUERY_PARAM_NAME_HIDE_USER_TYPE);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { redirectPathname } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isUserTypeJsRadioSelected, setIsUserTypeJsRadioSelected] = useState(true);
    const [accountType, setAccountType] = useState(!!userTypeQueryParam ? userTypeQueryParam : USER_TYPE_JS); // radio group MIU

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    // obtain user info from authUser
    const authUserId = authUser?.uid;
    const authUserDisplayName = authUser?.displayName;
    const authUserEmail = authUser?.email;
    const { firstName: authUserFirstName, lastName: authUserLastName } = getName(authUserDisplayName); // split name
    const providerId = _get(authUser, 'providerData[0].providerId');

    // attempt to obtain user info from 'redirect result', which should be available if this is the first time user sign in via social media;
    //  if not available, this is not the first time, so use the info from authUser instead.
    const userAuthProfile = authRedirectResult?.additionalUserInfo?.profile;
    const email = userAuthProfile?.email || authUserEmail;
    const firstName = userAuthProfile?.given_name || authUserFirstName;
    const lastName = userAuthProfile?.family_name || authUserLastName;

    //---------------------------------------------------------------------
    // Formik
    //---------------------------------------------------------------------
    const formik = useFormik({
        initialValues: {
            email,
            firstName,
            lastName,
        },
        validate: validate,
        validateOnBlur: false,
        validateOnChange: true,
        onSubmit: async values => {
            // console.log('submitting form...');
            // alert(JSON.stringify(values, null, 2));
            // const email = _get(values, 'email');
            // const firstName = _get(values, 'firstName');
            // const lastName = _get(values, 'lastName');
            //
            // console.log('go on', { email, firstName, lastName, isUserTypeJsRadioSelected });
            //
            await signUpWithSocialMedia({ userId: authUserId, providerId, data: values, isUserTypeJsRadioSelected: accountType === USER_TYPE_JS, history, redirectPathname });
            //
            // let isProceed = true;
            //
            // // if invitation mode, validate the email and account type first
            // if (isInvitationMode) {
            //     if (_toLower(email) !== _toLower(inviteeEmail)) {
            //         setConfirmCreateAccountTitle(CONFIRM_DIALOG_TITLE_CREATE_ACCOUNT_DIFF_EMAIL);
            //         setConfirmCreateAccountMessage(CONFIRM_DIALOG_MESSAGE_CREATE_ACCOUNT_DIFF_EMAIL);
            //         isProceed = false;
            //     } else if (accountType !== USER_TYPE_EMP) {
            //         setConfirmCreateAccountTitle(CONFIRM_DIALOG_TITLE_CREATE_JS_ACCOUNT);
            //         setConfirmCreateAccountMessage(CONFIRM_DIALOG_MESSAGE_CREATE_JS_ACCOUNT);
            //         isProceed = false;
            //     }
            // }
            //
            // if (isProceed) {
            //     await signUpWithEmail({ data: values, isUserTypeJsRadioSelected, isInvitationMode, invitationData, invitationCode, history });
            // }
        },
    });

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChange = event => {
        setAccountType(event.target.value);
    };

    const handleClickLogoutButton = () => {
        appContext.setIsAuthenticationPending(true);
        appContext.auth.signOut();
        if (appContext?.setCurrentUser) {
            appContext.setCurrentUser(null);
        }
        history.push(`${BASE_URL}${PATH_HOME}`, { isPromptLogin: true });
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, formik, isUserTypeJsRadioSelected, setIsUserTypeJsRadioSelected, handleClickLogoutButton });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider value={{ ...context, scrollToPageTop }}>
            <Grid {...defns.pageContainer}>
                <Grid {...defns.pageHeader}>
                    <TopNav />
                </Grid>
                <Grid {...defns.pageContent}>
                    <SimpleBar style={{ height: '100%', width: '100%', flex: 1 }} ref={pageContentSimpleBarEl}>
                        <Grid {...defns.tempContainer}>
                            <div {...defns.root}>
                                <form {...defns.createAccountForm}>
                                    <Grid>
                                        <label {...defns.fieldLabel} htmlFor="email">
                                            Email Address
                                        </label>
                                        <AfitdTextField {...defns.email} />
                                    </Grid>
                                    <Grid {...defns.formInputContainer}>
                                        <Grid {...defns.firstNameContainer}>
                                            <label {...defns.fieldLabel} htmlFor="firstName">
                                                First name
                                            </label>
                                            <br />
                                            <AfitdTextField placeholder="Ex. Jack" id="firstName" {...defns.firstName} />
                                        </Grid>

                                        <Grid {...defns.lastNameContainer}>
                                            <label {...defns.fieldLabel} htmlFor="lastName">
                                                Last name
                                            </label>
                                            <AfitdTextField placeholder="Ex. Sparrow" id="lastName" {...defns.lastName} />
                                        </Grid>
                                    </Grid>
                                    <Grid {...defns.checkBoxContainer}>
                                        <FormControl component="fieldset">
                                            <RadioGroup aria-label="report" name="report" value={accountType} onChange={handleChange}>
                                                <div {...defns.radioContainer}>
                                                    {(hideUserTypeQueryParam !== 'y' || userTypeQueryParam !== USER_TYPE_EMP) && (
                                                        <>
                                                            <FormControlLabel control={<Radio />} {...defns.userTypeJsRadioButton} />
                                                            <br />
                                                        </>
                                                    )}
                                                    {(hideUserTypeQueryParam !== 'y' || userTypeQueryParam !== USER_TYPE_JS) && (
                                                        <FormControlLabel control={<Radio />} {...defns.userTypeEmpRadioButton} />
                                                    )}
                                                </div>
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                    <div {...defns.createAccountButtonContainer}>
                                        <AfitdButton {...defns.createAccountButton}>Create Account</AfitdButton>
                                    </div>
                                    <div {...defns.logoutButtonContainer}>
                                        <AfitdButton {...defns.logoutButton}>Log Out</AfitdButton>
                                    </div>
                                </form>
                            </div>
                        </Grid>
                    </SimpleBar>
                </Grid>
            </Grid>
        </LayoutContext.Provider>
    );
};

export default CreateAccountLayout;
